import type { AuthUser } from '@arriba.digital/contracts';

/**
 * Session persistence.
 *
 * Tokens live in `localStorage` so an installed PWA survives a restart and
 * does not ask for credentials every time it is opened from the home screen.
 *
 * The tradeoff is real and deliberate: `localStorage` is readable by any
 * script on the origin, so an XSS bug becomes a token theft. The alternative
 * (httpOnly cookies) needs CSRF protection and a same-site deployment, which
 * conflicts with the "API and PWA on different hosts" topology this service
 * supports. The mitigation is the short access-token lifetime (15 min) plus
 * refresh-token rotation, so a stolen access token expires quickly.
 * See docs/architecture.md for the full reasoning.
 */

const ACCESS_KEY = 'arriba.digital.accessToken';
const REFRESH_KEY = 'arriba.digital.refreshToken';
const USER_KEY = 'arriba.digital.user';

function read(key: string): string | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    // Private browsing or a hardened profile can block storage entirely;
    // the app then behaves as a session-only client.
    return null;
  }

  /*
   * `"undefined"` and `"null"` are what a browser ends up holding when code
   * writes a value that was never there: `setItem(key, undefined)` stringifies
   * its argument. They are not tokens, but they are not null either, so
   * `isAuthenticated` would report a session and the app would send
   * `Authorization: Bearer undefined` — a request that is guaranteed to fail
   * and that looks, from the outside, exactly like an expired session.
   *
   * Treated as absent, so a browser already poisoned by an older build heals
   * itself instead of needing its storage cleared by hand.
   */
  if (raw === 'undefined' || raw === 'null') {
    return null;
  }

  return raw;
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    /* ignore */
  }
}

/**
 * A value fit to be a token.
 *
 * Deliberately strict, and deliberately not a cast: the whole point is to
 * refuse a shape the caller believed was a session. A silent write of
 * `"undefined"` produces a session that exists, is unusable, and cannot be
 * told apart from a real one — the worst of the three possible outcomes.
 */
function isToken(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

export const session = {
  get accessToken(): string | null {
    return read(ACCESS_KEY);
  },

  get refreshToken(): string | null {
    return read(REFRESH_KEY);
  },

  get user(): AuthUser | null {
    const raw = read(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  },

  /**
   * Store a token pair.
   *
   * Refuses anything that is not a non-empty string and clears the session
   * instead of storing a placeholder. A caller that reached here with garbage
   * has a bug, and the honest outcome is "not signed in" — never "signed in
   * with a token that cannot work".
   */
  setTokens(accessToken: unknown, refreshToken: unknown): void {
    if (!isToken(accessToken) || !isToken(refreshToken)) {
      this.clear();
      return;
    }

    write(ACCESS_KEY, accessToken);
    write(REFRESH_KEY, refreshToken);
  },

  setUser(user: AuthUser | null): void {
    write(USER_KEY, user ? JSON.stringify(user) : null);
  },

  clear(): void {
    write(ACCESS_KEY, null);
    write(REFRESH_KEY, null);
    write(USER_KEY, null);
  },

  get isAuthenticated(): boolean {
    return read(ACCESS_KEY) !== null;
  },
};
